import {
	ensureNotNil,
	isNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { openDocument } from "client_legacy/Documents/SharePoint/documentOpener";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { createRoot } from "react-dom/client";
import {
	attachToPopState,
	detachFromPopState,
	getState,
	PopStateHandler,
	replaceState
} from "client_legacy/ndaw/browserHistoryUtils";
import { getDocumentInformation } from "client/Api/Documents/SharePoint/sharepointDocuments";
import { AppStoreDialog } from "../Apps/Store/AppStoreDialog";
import { MainMenuItem, useMainMenuItemActivationHandlers } from "../Layout/mainMenuStore";
import { UserRequestDetailsDialog } from "../Settings/UserRequests/UserRequestDetailsDialog";
import { UserRequestsResponseDetailsDialog } from "../Settings/UserRequests/UserRequestResponseDetailsDialog";
import { DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { MakeBirthdayWishDialog } from "../Users/Birthday/MakeBirthdayWishDialog";
import { guid, Guid } from "client_legacy/ndaw/guid";
import { useActivityFeedStore } from "../ActivityFeed/store";
import {
	ActivityNavigationAction,
	ActivityNavigationActionKind,
	getActivityNavigationAction
} from "./activityNavigation";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { never } from "client_legacy/ndaw/errors";
import { parseQueryString } from "client_legacy/ndaw/urlHelper";
import { isIos, isSafari } from "client_legacy/ndaw/mobile";
import { NotificationSettingsDialog } from "../ActivityFeed/Settings/NotificationSettingsDialog";
import {
	generateFragment,
	HashNavigationRouteActionKind,
	HashNavigationRouteMatch,
	parseHash
} from "./routing";
import { getHubUrlForCommunity } from "../Hub/Communities/CommunitiesPage";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	HubManagementDialog,
	SelectedView as HubManagementSelectedView
} from "../Hub/Management/HubManagementDialog/HubManagementDialog";
import { useContactDialog } from "../AddressBook/ContactDialog";
import { ContactSource, getWorkspaceContactIdFromUserId } from "client/Api/Email/contacts";
import { HubDataSource } from "client/Api/Hub/hub";
import { useCommunityPostDialog } from "../Hub/Communities/CommunityPostDialog";
import { useHubDetailsDialog } from "../Hub/HubDetailsDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";

export const EnvironmentHashNavigation = (): ReactElement | null => {
	const [hash, setHash] = useState<string>(
		(): string => window.location.hash
	);

	const [isAppStoreVisible, setIsAppStoreVisible] = useState<boolean>(false);
	const [isNotificationSettingsVisible, setIsNotificationSettingsVisible] = useState<boolean>(false);
	const [hubManagementDialogState, setHubManagementDialogState] = useState<HubManagementDialogState>({ isVisible: false });
	const [userRequestDialogState, setUserRequestDialogState] = useState<UserRequestDialogState>({ isVisible: false });
	const [userRequestResponseDialogState, setUserRequestResponseDialogState] = useState<UserRequestDialogState>({ isVisible: false });
	const [makeBirthdayWishDialogState, setMakeBirthdayWishDialogState] = useState<MakeBirthdayWishDialogState>({isVisible: false});
	const [contactDialog, openContactDialog] = useContactDialog();
	const [communityPostDialog, openCommunityPostDialog] = useCommunityPostDialog();
	const [hubDetailsDialog, openHubDetailsDialog] = useHubDetailsDialog();

	useEffect(
		(): void => {
			const {
				pathname,
				search,
				hash
			} = window.location;

			if (hash) {
				return;
			}

			if (!search) {
				return;
			}

			const parseResult: TryResult<Partial<Record<"fragment", string>>> = parseQueryString(search, ["fragment"]);

			if (!parseResult.wasSuccessful || isNil(parseResult.result.fragment)) {
				return;
			}

			const newHash: string = `#${parseResult.result.fragment}`;

			const params = new URLSearchParams(search);
			params.delete("fragment");
			const newQueryString: string = params.toString();

			replaceState(
				getState(),
				"",
				pathname + (newQueryString ? `?${newQueryString}` : "") + newHash
			);

			setHash(newHash);
		},
		[]
	);

	const {
		activate: activateActivityFeed
	} = useMainMenuItemActivationHandlers(MainMenuItem.Activity);

	const {
		didInitiallyLoadFromBackend,
		activities
	} = useActivityFeedStore(
		store => ({
			didInitiallyLoadFromBackend: store.didInitiallyLoadFromBackend,
			activities: store.activities
		}),
		shallowComparison
	);

	const match = useMemo(
		(): HashNavigationRouteMatch | null => {
			return parseHash(hash);
		},
		[hash]
	);

	const matchedActivityBatchId = useMemo(
		(): Guid | undefined => {
			if (match && isOfAction(match, HashNavigationRouteActionKind.ActivityFeed)) {
				if (isNotNilOrEmpty(match.params.batchId)) {
					return guid(match.params.batchId);
				}
			}

			return undefined;
		},
		[match]
	);

	const clearHash = useCallback(
		(): void => {
			window.location.hash = "";

			// NOTE: safari does not trigger neither hashchange nor popstate events, when hash is set to empty string or #
			if (isSafari() || isIos()) {
				setHash("");
			}
		},
		[]
	);

	useEffect(
		() => {
			function popStateHandler(): void {
				setHash(window.location.hash);
			}

			const subscribedHandler: PopStateHandler = attachToPopState(popStateHandler);
			return (): void => detachFromPopState(subscribedHandler);
		},
		[]
	);

	useEffect(
		() => {
			if (!matchedActivityBatchId) {
				return;
			}

			const activityNavigationAction: ActivityNavigationAction = getActivityNavigationAction(
				matchedActivityBatchId,
				activities,
				didInitiallyLoadFromBackend
			);

			switch(activityNavigationAction.kind) {
				case ActivityNavigationActionKind.None:
					return;
				case ActivityNavigationActionKind.RedirectHash:
					if (isOfAction(activityNavigationAction.hashMatch, HashNavigationRouteActionKind.ActivityFeed)
						&& activityNavigationAction.hashMatch.params.batchId === matchedActivityBatchId
					) {
						throw new Error("Cyclic redirect detected.");
					}

					const fragment: string | null = generateFragment(activityNavigationAction.hashMatch);
					if (isNotNilOrEmpty(fragment)) {
						window.location.hash = `#${fragment}`;
					}

					return;
				case ActivityNavigationActionKind.OpenEmail:
					const emailClient: EmailClient = getEffectiveEmailClient();

					if (emailClient.supportsNavigateToEmail) {
						emailClient.navigateToEmail({
							emailId: activityNavigationAction.emailId
						});
					} else {
						activateActivityFeed();
					}

					clearHash();
					return;
				case ActivityNavigationActionKind.OpenBirthdayWishDialog:
					const {
						userId,
						firstName,
						lastName
					} = activityNavigationAction;

					setMakeBirthdayWishDialogState({
						isVisible: true,
						firstName,
						lastName,
						userId
					});

					clearHash();
					return;
				case ActivityNavigationActionKind.OpenActivityFeed:
					activateActivityFeed();

					clearHash();
					return;
				default:
					never(activityNavigationAction);
			}
		},
		[
			activateActivityFeed,
			activities,
			clearHash,
			didInitiallyLoadFromBackend,
			matchedActivityBatchId
		]
	);

	useEffect(
		() => {
			const cancellation = new AbortController();

			async function processHashAction(signal: AbortSignal): Promise<void> {
				if (isNil(match) || isNotNilOrEmpty(matchedActivityBatchId)) {
					return undefined;
				}

				if (isOfAction(match, HashNavigationRouteActionKind.ActivityFeed)) {
					activateActivityFeed();
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.NotificationSettings)) {
					setIsNotificationSettingsVisible(true);
				} else if (isOfAction(match, HashNavigationRouteActionKind.Announcement)) {
					await openHubDetailsDialog({
						itemKind: HubItemKind.Announcement,
						id: ensureNotNilNumber(match.params.announcementId)
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.AnnouncementComment)) {
					await openHubDetailsDialog({
						itemKind: HubItemKind.Announcement,
						id: ensureNotNilNumber(match.params.announcementId),
						commentId: ensureNotNilNumber(match.params.commentId)
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.KnowledgeItem)) {
					await openHubDetailsDialog({
						itemKind: HubItemKind.KnowledgeItem,
						id: ensureNotNilNumber(match.params.knowledgeItemId)
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.KnowledgeItemComment)) {
					await openHubDetailsDialog({
						itemKind: HubItemKind.KnowledgeItem,
						id: ensureNotNilNumber(match.params.knowledgeItemId),
						commentId: ensureNotNilNumber(match.params.commentId)
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.Event)) {
					await openHubDetailsDialog({
						itemKind: HubItemKind.Event,
						id: ensureNotNilNumber(match.params.eventId)
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.EventComment)) {
					await openHubDetailsDialog({
						itemKind: HubItemKind.Event,
						id: ensureNotNilNumber(match.params.eventId),
						commentId: ensureNotNilNumber(match.params.commentId)
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.CommunityPostComment)) {
					const { postId, commentId } = match.params;

					await openCommunityPostDialog({
						id: ensureNotNilNumber(postId),
						deepLink: {
							kind: "comment",
							commentId: ensureNotNilNumber(commentId)
						}
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.CommunityPost)) {
					const { postId } = match.params;

					await openCommunityPostDialog({
						id: ensureNotNilNumber(postId)
					});
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.CommunityFeed)) {
					const { communityId } = match.params;

					navigateTo(
						getHubUrlForCommunity({
							source: HubDataSource.Community,
							communityId: ensureNotNilNumber(communityId)
						})
					);
				} else if (isOfAction(match, HashNavigationRouteActionKind.AppStore)) {
					setIsAppStoreVisible(true);
				} else if (isOfAction(match, HashNavigationRouteActionKind.OpenDocument)) {
					const {
						siteUrl,
						listId,
						itemId
					} = match.queryParams;
					const document: DocumentItemViewModel = await getDocumentInformation(
						{
							siteUrl: ensureNotNil(siteUrl),
							listId: guid(ensureNotNil(listId))
						},
						ensureNotNilNumber(itemId),
						signal
					);
					openDocument(document);
					clearHash();
				} else if (isOfAction(match, HashNavigationRouteActionKind.UserRequestDetails)) {
					setUserRequestDialogState({
						isVisible: true,
						requestId: ensureNotNilNumber(match.params.requestId)
					});
				} else if (isOfAction(match, HashNavigationRouteActionKind.UserRequestResponseDetails)) {
					setUserRequestResponseDialogState({
						isVisible: true,
						requestId: ensureNotNilNumber(match.params.requestId)
					});
				} else if (isOfAction(match, HashNavigationRouteActionKind.HubManagement)) {
					setHubManagementDialogState({
						isVisible: true,
						view: undefined
					});
				} else if (isOfAction(match, HashNavigationRouteActionKind.HubManagementSpecificView)) {
					const {
						hubItemKind,
						view
					} = match.params;

					let selectedView: HubManagementSelectedView | undefined = undefined;

					if (isNotNilOrEmpty(hubItemKind) && isNotNilOrEmpty(view)) {
						selectedView = `${hubItemKind}.${view}` as HubManagementSelectedView;
					}

					setHubManagementDialogState({
						isVisible: true,
						view: selectedView
					});
				} else if (isOfAction(match, HashNavigationRouteActionKind.UserDetails)) {
					const {
						userId
					} = match.params;

					await openContactDialog({
						source: ContactSource.Workspace,
						initialView: {
							kind: "details",
							contactId: getWorkspaceContactIdFromUserId(ensureNotNilNumber(userId))
						}
					});
					clearHash();
				}
			};

			processHashAction(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			activateActivityFeed,
			clearHash,
			match,
			matchedActivityBatchId,
			openContactDialog,
			openCommunityPostDialog,
			openHubDetailsDialog
		]
	);

	return (
		<>
			{isAppStoreVisible && (
				<AppStoreDialog
					onClose={() => {
						setIsAppStoreVisible(false);
						clearHash();
					}}
				/>
			)}
			{userRequestDialogState.isVisible && (
				<UserRequestDetailsDialog
					id={userRequestDialogState.requestId}
					onClose={() => {
						setUserRequestDialogState({ isVisible: false });
						clearHash();
					}}
					onResponded={() => {
						setUserRequestDialogState({ isVisible: false });
						clearHash();
					}}
				/>
			)}
			{userRequestResponseDialogState.isVisible && (
				<UserRequestsResponseDetailsDialog
					id={userRequestResponseDialogState.requestId}
					onClose={() => {
						setUserRequestResponseDialogState({ isVisible: false });
						clearHash();
					}}
				/>
			)}
			{makeBirthdayWishDialogState.isVisible && (
				<MakeBirthdayWishDialog
					userId={makeBirthdayWishDialogState.userId}
					firstName={makeBirthdayWishDialogState.firstName}
					lastName={makeBirthdayWishDialogState.lastName}
					onClose={() => {
						setMakeBirthdayWishDialogState({ isVisible: false });
						clearHash();
					}}
					onSend={() => {
						setMakeBirthdayWishDialogState({ isVisible: false });
						clearHash();
					}}
				/>
			)}
			{isNotificationSettingsVisible && (
				<NotificationSettingsDialog
					onClose={() => {
						setIsNotificationSettingsVisible(false);
						clearHash();
					}}
				/>
			)}
			{hubManagementDialogState.isVisible && (
				<HubManagementDialog
					onClose={() => {
						setHubManagementDialogState({ isVisible: false });
						clearHash();
					}}
					initialView={hubManagementDialogState.view}
				/>
			)}
			{contactDialog}
			{communityPostDialog}
			{hubDetailsDialog}
		</>
	);
};

EnvironmentHashNavigation.displayName = "EnvironmentHashNavigation";

export function init(): void {
	createRoot(
		ensureNotNil(
			document.getElementById("hash-navigation-host")
		)
	)
		.render(<EnvironmentHashNavigation />);
}

function isOfAction<TKind extends HashNavigationRouteActionKind>(
	match: HashNavigationRouteMatch,
	action: TKind
): match is HashNavigationRouteMatch<TKind> {
	return match.action === action;
}


type UserRequestDialogState = {
	isVisible: false;
	requestId?: undefined;
} | {
	isVisible: true;
	requestId: number;
};

type MakeBirthdayWishDialogState = {
	isVisible: false;
	userId?: undefined;
	firstName?: undefined;
	lastName?: undefined;
} | {
	isVisible: true;
	userId: number;
	firstName: string;
	lastName: string;
};

type HubManagementDialogState = {
	isVisible: false;
} | {
	isVisible: true;
	view: HubManagementSelectedView | undefined;
};

function ensureNotNilNumber(strValue: string | nil): number {
	if (!strValue) {
		throw new Error("Value is nil or empty");
	}

	const result: number = parseInt(strValue, 10);
	if (isNaN(result) || result.toString(10) !== strValue) {
		throw new Error("Value is not a number");
	}

	return result;
}
